import { useSyncExternalStore } from 'react';

/**
 * Theme setting (DESIGN.md §2): System follows `prefers-color-scheme`; Light and Dark set
 * `data-theme` on `<html>`, which tokens.css honours. Saved per device. public/theme-init.js
 * applies it before the first paint.
 */
export const THEMES = ['system', 'light', 'dark'] as const;
export type Theme = (typeof THEMES)[number];

const STORAGE_KEY = 'dishly.theme';
/** `--bg` per theme (meta tags can't read CSS variables). */
const BACKGROUND = { light: '#F6F1E8', dark: '#1B1714' } as const;

const listeners = new Set<() => void>();

export function getTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // Storage unavailable.
  }
  return 'system';
}

/** Sets `data-theme` and the browser chrome colour for `theme`. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);

  const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  for (const meta of metas) {
    const media = meta.dataset.media ?? meta.media;
    meta.dataset.media = media;
    if (theme === 'system') {
      meta.media = media;
      meta.content = media.includes('dark') ? BACKGROUND.dark : BACKGROUND.light;
    } else {
      meta.media = '';
      meta.content = BACKGROUND[theme];
    }
  }
}

export function setTheme(theme: Theme): void {
  try {
    if (theme === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Not saved; still applied for this visit.
  }
  applyTheme(theme);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, getTheme);
}
